import { alertController } from '@ionic/vue'
import type { APIException } from '@verein-checkin/shared/types/APIException'

const getBody = <T>(c: Response | Request): Promise<T> => {
	const contentType = c.headers.get('content-type')

	if (contentType && (contentType.includes('application/json') || contentType?.includes('application/problem+json'))) {
		return c.json()
	}

	return c.text() as Promise<T>
}

const getUrl = (contextUrl: string): string => {
	const config = useRuntimeConfig().public
	const { isLocal } = useEnvironment()
	const { resolvedTenant, tenantsFetched } = useTenant()

	// Lokale Entwicklung: apiBaseUrl ersetzt den Tenant komplett (alle Mandanten teilen sich
	// einen Backend-Host) – Dev-Tools wie das Anlegen eines Testnutzers funktionieren so
	// auch, wenn auf der Login-Seite noch keine Sparkasse ausgewählt wurde.
	if (isLocal && config.apiBaseUrl) {
		return `${config.apiBaseUrl}${contextUrl}`
	}

	if (!tenantsFetched.value) {
		throw createError({
			statusCode: 503,
			statusMessage: 'Tenant data has not been loaded yet. Ensure fetchTenants() runs before any API call.',
		})
	}

	if (!resolvedTenant.value?.baseUrl) {
		throw createError({
			statusCode: 400,
			statusMessage: 'No tenant selected',
		})
	}

	return `${resolvedTenant.value.baseUrl}${contextUrl}`
}

/**
 * Custom Fetcher mit Überprüfungen.
 * @param url
 * @param options
 */
export const customFetchProtected = async <T>(url: string, options: RequestInit) => {
	const {
		isAuthenticated,
		isExpired,
		refreshToken,
		signOut,
	} = useAuth()
	const { hasAccount } = useAccount()

	if (!isAuthenticated.value || !hasAccount.value) {
		throw createError({
			statusCode: 401,
			statusMessage: 'User is not authenticated or has no account',
		})
	}

	if (isExpired.value) {
		console.debug('JWT expired, refreshing token...')

		try {
			await refreshToken()
		} catch (error) {
			console.error('Token refresh failed:', error)

			// Globalen Lade-Spinner deaktivieren, damit Alert nicht verdeckt und die UI
			// nach signOut nicht im Loading-Zustand haengen bleibt.
			useApp().loading.value = false

			const alert = await alertController.create({
				header: 'Ihre Sitzung ist abgelaufen',
				message: 'Bitte melden Sie sich erneut an.',
				buttons: ['OK'],
			})

			await alert.present()
			await alert.onDidDismiss()
			await signOut()

			// Anstehenden Request abbrechen, damit der Aufrufer den Fehler propagiert
			// und in-flight handleAsync-Aufrufe ihren finally-Block ausfuehren.
			throw createError({
				statusCode: 401,
				statusMessage: 'Session expired',
			})
		}
	}

	return customFetch<T>(url, options)
}

export const customFetch = async <T>(url: string, options: RequestInit) => {
	const { jwt } = useAuth()

	const headers = {
		...(options.headers || {}),
		...(jwt.value ? { Authorization: `Bearer ${jwt.value}` } : {}),
	}

	const updatedOptions: RequestInit = {
		...options,
		headers,
	}

	const response = await fetch(getUrl(url), updatedOptions)

	if (!response.ok) {
		throw await getBody<APIException>(response)
	}

	return { status: response.status, data: await getBody<T>(response), headers: response.headers } as T
}
